import { Injectable } from '@angular/core';
import { AuthActionType, AuthError, AuthErrorCode, AuthService } from '@coach-bot/data-access/auth';
import { User, UserDetails, UserStore, ViewStore, isUserStudent } from '@coach-bot/data-access/shared';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthState {
  readonly error$ = new Subject<string>();
  readonly sendResetLinkSuccessfull$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private userStore: UserStore,
    private viewStore: ViewStore
  ) {}

  async signIn(email: string, password: string): Promise<void> {
    let user: User;
    try {
      user = await this.authService.signIn(email, password);
      if (!isUserStudent(user)) {
        throw new AuthError(AuthActionType.SignIn, AuthErrorCode.NotAllowedException);
      }
    } catch (err: unknown) {
      if (user) {
        await this.authService.signOut();
      }
      this.handleError(err);
    }
  }

  async signOut(): Promise<void> {
    await this.authService.signOut();
  }

  async forgotPassword(email: string): Promise<void> {
    try {
      this.viewStore.setAuthLoading(true);
      await this.authService.forgotPassword(email);
      this.sendResetLinkSuccessfull$.next();
    } catch (err: unknown) {
      this.handleError(err);
    } finally {
      this.viewStore.setAuthLoading(false);
    }
  }

  async loadUserDetails(userId: string): Promise<UserDetails> {
    try {
      const userDetails: UserDetails = await this.authService.getUserDetails(userId);
      await this.userStore.setUserDetails(userDetails);
      return userDetails;
    } catch (err: any) {
      console.error('Load user details error:', err);
    }
  }

  async handleUnauthorizedError(): Promise<void> {
    if (this.userStore.isSignedIn$.value) {
      await this.signOut();
    }
  }

  private handleError(error: unknown): void {
    if (error instanceof AuthError) {
      this.error$.next(error.message);
    }
  }
}
